import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from '../../../components/ui';
import { useToast } from '../../../contexts/ToastContext';
import { materialLedgerService } from '../../../services/materialLedgerService';
import { activeLedgerFilterCount, exportWindowProblem, thisMonth, type LedgerFilters } from '../ledger';

interface ExportLedgerModalProps {
  show: boolean;
  /** The page's filters: the export keeps them and asks only for the dates. */
  filters: LedgerFilters;
  onHide: () => void;
}

/** "Export CSV" on the ledger: a date window (required, at most a year), plus whatever the page is filtered to. */
const ExportLedgerModal: React.FC<ExportLedgerModalProps> = ({ show, filters, onHide }) => {
  const { showToast } = useToast();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Each opening starts from the page's dates, or this month when the page has none.
  useEffect(() => {
    if (!show) return;
    const month = thisMonth();
    setFrom(filters.from || month.from);
    setTo(filters.to || month.to);
    setError(null);
  }, [show, filters.from, filters.to]);

  const problem = exportWindowProblem(from, to);
  const otherFilters = activeLedgerFilterCount({ ...filters, from: '', to: '' });

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const fileName = await materialLedgerService.exportCsv({ ...filters, from, to });
      showToast(`Downloaded ${fileName}`);
      onHide();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export the ledger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={busy ? () => undefined : onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Export the ledger</Modal.Title>
      </Modal.Header>
      <Modal.Body className="tw-space-y-4">
        <p className="tw-mb-0 tw-text-sm tw-text-content-secondary">
          One row per movement, oldest first, with dates in local time.
          {otherFilters
            ? ` It keeps the ${otherFilters === 1 ? 'filter' : `${otherFilters} filters`} applied on the page.`
            : ' Every site and material you can see is included.'}
        </p>
        <div className="tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2">
          <Form.Group controlId="ledger-export-from">
            <Form.Label>From</Form.Label>
            <Form.Control type="date" value={from} max={to || undefined} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFrom(event.target.value)} />
          </Form.Group>
          <Form.Group controlId="ledger-export-to">
            <Form.Label>To (inclusive)</Form.Label>
            <Form.Control type="date" value={to} min={from || undefined} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTo(event.target.value)} />
          </Form.Group>
        </div>
        {error ? <Alert variant="danger" className="tw-mb-0">{error}</Alert> : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => void download()} disabled={busy || problem !== null} disabledReason={busy ? undefined : problem ?? undefined}>
          {busy ? <Spinner animation="border" size="sm" /> : <i className="bi bi-download" aria-hidden="true" />}
          Download CSV
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExportLedgerModal;
